import { useCallback, useEffect, useState } from "react";
import { Download, X, Shield, History, Package } from "lucide-react";
import { osStore } from "../os/store";
import { dialogStore } from "../os/dialog";
import { osCan, pkgUpdate, pkgUpdates } from "../os/api";
import type { PackageUpdate } from "@arclog/module-sdk";

type Notice = {
  id: string;
  kind: string;
  updateId: string;
  title: string;
  body: string;
  toVersion?: string;
  createdAt: string;
};

function authHeaders(): HeadersInit {
  const t = localStorage.getItem("arclog.token");
  return t
    ? { Authorization: "Bearer " + t, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

/**
 * Menubar update badge — notifications only. Install is always explicit.
 *
 * This surfaces TWO independent update systems, which is the thing to hold onto
 * when reading it:
 *
 * - **OS updates** (`/api/os/update/*`) — the whole machine moves. Snapshotted
 *   first, rolled back through Time Machine, and applied by the server.
 * - **App updates** (`pkgUpdates`/`pkgUpdate`) — one module's `defaultVersion`
 *   advances. NOT snapshotted, and the `migrate()` hook runs in this browser,
 *   which is why the apply path lives in the shell at all.
 *
 * They were built separately and only the OS one was ever surfaced here, so an
 * app could sit stale for an entire development arc with the menubar showing a
 * clean bill of health — the exact staleness `28-app-updates.md` exists to end.
 * Rows are labelled by kind because the guarantees differ: promising a snapshot
 * on a row that gets none would be a lie, and a rollback the user then cannot
 * perform is worse than no promise at all.
 */
export function UpdateCenter() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Notice[]>([]);
  const [apps, setApps] = useState<PackageUpdate[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Per-app failures, by module id. Bulk update must name what it could not do. */
  const [appErrors, setAppErrors] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    // Independent of the OS check below: pkgUpdates() never rejects by contract,
    // so a failing OS endpoint must not also blank the app rows (and vice versa).
    void pkgUpdates().then(setApps);
    try {
      await fetch("/api/os/update/check", { headers: authHeaders() });
      const res = await fetch("/api/os/update/notifications", { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setNotes(data.notifications || data.inbox || []);
    } catch {
      /* */
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const dismiss = async (id: string) => {
    await fetch("/api/os/update/notifications/" + encodeURIComponent(id) + "/dismiss", {
      method: "POST",
      headers: authHeaders(),
    });
    await refresh();
  };

  const install = async (n: Notice) => {
    setError(null);
    const ok = await dialogStore.confirm({
      title: "Install update?",
      message:
        (n.title || n.updateId) +
        "\n\nA snapshot (Time Machine) is taken first so you can roll back. " +
        "Nothing installs until you confirm.",
      okLabel: "Install",
      cancelLabel: "Not now",
    });
    if (!ok) return;
    setBusy(n.id);
    setPhase("Snapshotting current state…");
    // The server install is one synchronous call (snapshot → stage → apply), so
    // this is an honest indeterminate bar with the phases named — not a fake
    // percentage. Without it the button looked inert for the whole request.
    const phaseTimers = [
      window.setTimeout(() => setPhase("Staging update artifacts…"), 600),
      window.setTimeout(() => setPhase("Applying version marker…"), 1400),
    ];
    try {
      const res = await fetch("/api/os/update/install", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ id: n.updateId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      osStore.notify(
        "Installed " + n.updateId + (data.snapshot?.id ? " · snapshot " + data.snapshot.id : ""),
        "success"
      );
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Inline as well as a toast: a toast that scrolls past reads as "the
      // button did nothing", which is exactly how this failure was reported.
      setError(msg);
      osStore.notify(msg, "error");
      try {
        void fetch("/api/system/logs", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            source: "shell.update-center",
            level: "error",
            message: "Update install UI error: " + msg,
            code: "UI_INSTALL_ERROR",
            detail: { updateId: n.updateId },
          }),
        });
      } catch {
        /* */
      }
    } finally {
      for (const t of phaseTimers) clearTimeout(t);
      setBusy(null);
      setPhase(null);
    }
  };

  /**
   * Apply one app update. Returns the failure message rather than throwing, so
   * the bulk caller below can keep going — one broken `migrate()` must not
   * strand the other apps on old versions.
   *
   * On success the row is NOT removed locally; `refresh()` re-asks the server.
   * If the update somehow did not take, the row is still there on the next
   * paint, which is the same rule the App Store card follows.
   */
  const applyApp = useCallback(async (u: PackageUpdate): Promise<string | null> => {
    try {
      await pkgUpdate(u.id);
      setAppErrors((prev) => {
        const next = { ...prev };
        delete next[u.id];
        return next;
      });
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Named inline against the row, and it names the version still running —
      // a toast alone lets someone walk away believing an update landed.
      setAppErrors((prev) => ({
        ...prev,
        [u.id]: `Update to ${u.available} failed — still running ${u.current}. ${msg}`,
      }));
      return msg;
    }
  }, []);

  const updateApp = async (u: PackageUpdate) => {
    setBusy(u.id);
    setPhase(`Updating ${u.name} to ${u.available}…`);
    const failure = await applyApp(u);
    setBusy(null);
    setPhase(null);
    if (!failure) osStore.notify(`Updated ${u.name} to ${u.available}`, "success");
    else osStore.notify(`Update failed: ${failure}`, "error");
    await refresh();
  };

  /**
   * Update every app with one click.
   *
   * Sequential, not `Promise.all`: each apply is install → migrate → advance
   * default against the same registry, and the migrations run here in this tab.
   * Overlapping them races the default-version write and makes a partial failure
   * impossible to attribute.
   *
   * The result is reported per app, never as a bare "done". A bulk button that
   * says "Updated all" while having silently skipped two is precisely the
   * failure this project treats as its worst: a green light over a broken thing.
   */
  const updateAllApps = async () => {
    const targets = apps.slice();
    const ok = await dialogStore.confirm({
      title: `Update ${targets.length} apps?`,
      message:
        targets.map((u) => `${u.name}  ${u.current} → ${u.available}`).join("\n") +
        "\n\nEach app installs its new version beside the old one and migrates in place. " +
        "No snapshot is taken — an app that fails keeps running the version it is on now.",
      okLabel: `Update ${targets.length}`,
      cancelLabel: "Not now",
    });
    if (!ok) return;

    setBusy("__all__");
    const failed: string[] = [];
    for (const [i, u] of targets.entries()) {
      setPhase(`Updating ${u.name} (${i + 1} of ${targets.length})…`);
      const failure = await applyApp(u);
      if (failure) failed.push(u.name);
    }
    setBusy(null);
    setPhase(null);

    const done = targets.length - failed.length;
    if (!failed.length) osStore.notify(`Updated ${done} apps`, "success");
    else
      osStore.notify(
        `Updated ${done} of ${targets.length} — ${failed.join(", ")} failed`,
        failed.length === targets.length ? "error" : "warn"
      );
    await refresh();
  };

  // An update is an install of a new version, machine-wide — same gate the App
  // Store card uses. Say so before the click rather than after a 403.
  const mayUpdateApps = osCan("os.packages.install");
  const count = notes.length + apps.length;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      const root = document.querySelector(".update-center");
      if (root && t && !root.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="update-center">
      <button
        type="button"
        className={"icon-btn update-bell" + (count ? " has" : "")}
        title={count ? count + " update(s) available" : "No pending updates"}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((v) => !v);
          void refresh();
        }}
      >
        <Download size={14} strokeWidth={2} />
        {count > 0 && <span className="update-badge">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <div className="update-panel glass glass-strong" role="dialog" aria-label="Updates">
          <div className="update-panel-h">
            <strong>Updates</strong>
            {/* Was "snapshot before install" — true of OS updates, false of app
                updates, and this header sits above both. The snapshot promise
                now lives on the rows that can keep it. */}
            <span className="muted">Opt-in only · nothing installs until you confirm</span>
            <button type="button" className="icon-btn" onClick={() => setOpen(false)}>
              <X size={14} />
            </button>
          </div>
          {!count && (
            <div className="update-empty">You are up to date. Checks never install automatically.</div>
          )}

          {apps.length > 0 && (
            <>
              <div className="update-section">
                <span>Apps</span>
                {/* Only worth its own control for more than one app: a single
                    row's own button already names both versions, which "Update
                    all" cannot. */}
                {apps.length > 1 && mayUpdateApps && (
                  <button
                    type="button"
                    className="os-btn primary update-all"
                    disabled={busy !== null}
                    onClick={() => void updateAllApps()}
                  >
                    {busy === "__all__" ? "Updating…" : `Update all ${apps.length}`}
                  </button>
                )}
              </div>
              {busy === "__all__" && (
                <div className="update-progress" role="status" aria-live="polite">
                  <div className="update-progress-bar">
                    <i />
                  </div>
                  {/* Names the app being worked on, not a percentage — the count
                      is known but each apply is one opaque server round-trip. */}
                  <span className="update-progress-label">{phase || "Working…"}</span>
                </div>
              )}
              {apps.map((u) => (
                <div key={u.id} className="update-row">
                  <div className="update-row-t">
                    <Package size={14} />
                    <div>
                      <div className="update-title">
                        {u.name} <span className="update-ver">{u.current} → {u.available}</span>
                      </div>
                      <div className="update-body">{u.notes || `${u.id} · updates in place`}</div>
                    </div>
                  </div>
                  <div className="update-actions">
                    <button
                      type="button"
                      className="os-btn primary"
                      disabled={busy !== null || !mayUpdateApps}
                      title={
                        mayUpdateApps
                          ? undefined
                          : "Requires os.packages.install — an admin has to apply this update"
                      }
                      onClick={() => void updateApp(u)}
                    >
                      {busy === u.id ? "Updating…" : `Update to ${u.available}`}
                    </button>
                  </div>
                  {!mayUpdateApps && (
                    <div className="update-body">
                      An admin has to apply this one — updating installs a new version for the
                      whole machine.
                    </div>
                  )}
                  {appErrors[u.id] && (
                    <div className="update-error" role="alert">
                      {appErrors[u.id]}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {notes.length > 0 && apps.length > 0 && <div className="update-section">System</div>}
          {notes.map((n) => (
            <div key={n.id} className="update-row">
              <div className="update-row-t">
                <Shield size={14} />
                <div>
                  <div className="update-title">{n.title}</div>
                  <div className="update-body">{n.body}</div>
                </div>
              </div>
              <div className="update-actions">
                <button
                  type="button"
                  className="os-btn primary"
                  disabled={busy === n.id}
                  onClick={() => void install(n)}
                >
                  {busy === n.id ? "Installing…" : "Install"}
                </button>
                <button
                  type="button"
                  className="os-btn ghost"
                  disabled={busy === n.id}
                  onClick={() => void dismiss(n.id)}
                >
                  Later
                </button>
              </div>
              {busy === n.id && (
                <div className="update-progress" role="status" aria-live="polite">
                  <div className="update-progress-bar">
                    <i />
                  </div>
                  <span className="update-progress-label">{phase || "Working…"}</span>
                </div>
              )}
              {error && busy !== n.id && (
                <div className="update-error" role="alert">
                  Install failed — {error}
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            className="update-link"
            onClick={() => {
              setOpen(false);
              osStore.openApp("app.settings", "Settings");
            }}
          >
            <History size={12} /> Snapshots & update settings…
          </button>
        </div>
      )}
    </div>
  );
}
